<template>
  <Form v-if="projectDeveloperForm"
        @submit="submit"
        :initial-values="projectDeveloperForm"
        :validation-schema="projectDeveloperSchema">
    <section class="application-create__form">
      <div class="container-xxxl">
        <div class="row">
          <div class="col-xxl-6 offset-xxl-3">
            <h3 class="application__form__title">{{ $lang.app.PLANNER_INFORMATION }}</h3>
            <div class="application__form__block">
              <div class="input__group">
                <label for="email-input" class="label">{{ $lang.app.EMAIL }}</label>
                <div class="input__wrapper">
                  <Field name="email" v-slot="{ field }" v-model="projectDeveloperForm.email">
                    <input v-bind="field"
                           type="email"
                           id="email-input"
                           class="input"
                           :placeholder="$lang.app.ENTER">
                  </Field>
                  <button type="button" class="input__button" @click="findProjectDeveloper">{{ $lang.app.FIND }}</button>
                </div>
                <div v-if="projectDeveloperInfo"
                     class="flex items-center mt-2 rounded-md text-white bg-green-400 px-4 py-2 font-medium text-lg">
                  <CheckIcon class="w-10 h-10 flex-shrink-0 mr-4"/>
                  <div>
                    <p>{{ $lang.app[userTypesGetLabelRefByValue(projectDeveloperInfo.type)] }}</p>
                    <p>{{ projectDeveloperInfo.username }}</p>
                  </div>
                </div>
                <ErrorMessage :name="`email`" class="text-xs text-red-400"/>
              </div>
              <div class="row">
                <div class="col-lg-6">
                  <div class="input__group">
                    <label for="type-input" class="label">{{ $lang.app.TYPE }} <span class="text-red-500">*</span></label>
                    <div class="select__wrapper">
                      <Field name="type" v-slot="{ field, errorMessage }" v-model="projectDeveloperForm.type">
                        <Multiselect
                            id="type-input"
                            :class="{'select--fail': errorMessage }"
                            v-bind="field"
                            :options="types"
                            :placeholder="$lang.app.SELECT_FROM_OPTIONS"
                            :searchable="true"
                            :noOptionsText="$lang.app.LIST_EMPTY"
                            :noResultsText="$lang.app.NOT_FOUND"
                            :can-clear="false">
                        </Multiselect>
                        <span v-if="errorMessage" class="input__fail">{{ errorMessage }}</span>
                      </Field>
                    </div>
                  </div>
                </div>
                <div class="col-lg-6">
                  <div class="input__group">
                    <label for="cert-number-input" class="label">{{ $lang.app.CERTIFICATE_NUMBER_LICENSES }} <span class="text-red-500">*</span></label>
                    <Field name="certNumber" v-slot="{ field, errorMessage }" v-model="projectDeveloperForm.certNumber">
                      <input v-bind="field"
                             type="text"
                             id="cert-number-input"
                             class="input"
                             :class="{'input--fail': errorMessage }"
                             :placeholder="$lang.app.ENTER">
                      <span v-if="errorMessage" class="input__fail">{{ errorMessage }}</span>
                    </Field>
                  </div>
                </div>
              </div>
              <div v-if="projectDeveloperForm.type === userTypesGetValueByKey('INDIVIDUAL_ENTITY')
                    || projectDeveloperForm.type === userTypesGetValueByKey('INDIVIDUAL_ENTREPRENEUR')">
                <div class="row">
                  <div class="col-lg-6">
                    <div class="input__group">
                      <label for="person-pin-input" class="label">{{ $lang.app.PIN }}</label>
                      <div class="select__wrapper">
                        <Field name="person.pin" v-slot="{ field, errorMessage }" v-model="projectDeveloperForm.person.pin">
                          <input v-bind="field"
                                 type="text"
                                 id="person-pin-input"
                                 class="input"
                                 :class="{'input--fail': errorMessage }"
                                 :placeholder="$lang.app.ENTER">
                          <span v-if="errorMessage" class="input__fail">{{ errorMessage }}</span>
                        </Field>
                      </div>
                    </div>
                  </div>
                  <div class="col-lg-6">
                    <div class="input__group">
                      <label for="person-surname-input" class="label">{{ $lang.app.SURNAME }} <span class="text-red-500">*</span></label>
                      <Field name="person.surname" v-slot="{ field, errorMessage }" v-model="projectDeveloperForm.person.surname">
                        <input v-bind="field"
                               type="text"
                               id="person-surname-input"
                               class="input"
                               :class="{'input--fail': errorMessage }"
                               :placeholder="$lang.app.ENTER">
                        <span v-if="errorMessage" class="input__fail">{{ errorMessage }}</span>
                      </Field>
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col-lg-6">
                    <div class="input__group">
                      <label for="person-name-input" class="label">{{ $lang.app.NAME }} <span class="text-red-500">*</span></label>
                      <div class="select__wrapper">
                        <Field name="person.name" v-slot="{ field, errorMessage }" v-model="projectDeveloperForm.person.name">
                          <input v-bind="field"
                                 type="text"
                                 id="person-name-input"
                                 class="input"
                                 :class="{'input--fail': errorMessage }"
                                 :placeholder="$lang.app.ENTER">
                          <span v-if="errorMessage" class="input__fail">{{ errorMessage }}</span>
                        </Field>
                      </div>
                    </div>
                  </div>
                  <div class="col-lg-6">
                    <div class="input__group">
                      <label for="person-patronymic-input" class="label">{{ $lang.app.PATRONYMIC }}</label>
                      <Field name="person.patronymic" v-slot="{ field, errorMessage }" v-model="projectDeveloperForm.person.patronymic">
                        <input v-bind="field"
                               type="text"
                               id="person-patronymic-input"
                               class="input"
                               :class="{'input--fail': errorMessage }"
                               :placeholder="$lang.app.ENTER">
                        <span v-if="errorMessage" class="input__fail">{{ errorMessage }}</span>
                      </Field>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="projectDeveloperForm.type === userTypesGetValueByKey('LEGAL_ENTITY')">
                <div class="row">
                  <div class="col-lg-6">
                    <div class="input__group">
                      <label for="organization-inn-input" class="label">{{ $lang.app.INN }}</label>
                      <div class="select__wrapper">
                        <Field name="organization.inn" v-slot="{ field, errorMessage }" v-model="projectDeveloperForm.organization.inn">
                          <input v-bind="field"
                                 type="text"
                                 id="organization-inn-input"
                                 class="input"
                                 :class="{'input--fail': errorMessage }"
                                 :placeholder="$lang.app.ENTER">
                          <span v-if="errorMessage" class="input__fail">{{ errorMessage }}</span>
                        </Field>
                      </div>
                    </div>
                  </div>
                  <div class="col-lg-6">
                    <div class="input__group">
                      <label for="organization-name-kg-input" class="label">{{ $lang.app.ORG_NAME_KG }} <span class="text-red-500">*</span></label>
                      <Field name="organization.nameKg" v-slot="{ field, errorMessage }" v-model="projectDeveloperForm.organization.nameKg">
                        <input v-bind="field"
                               type="text"
                               id="organization-name-kg-input"
                               class="input"
                               :class="{'input--fail': errorMessage }"
                               :placeholder="$lang.app.ENTER">
                        <span v-if="errorMessage" class="input__fail">{{ errorMessage }}</span>
                      </Field>
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col-lg-6">
                    <div class="input__group">
                      <label for="organization-name-ru-input" class="label">{{ $lang.app.ORG_NAME_RU }} <span class="text-red-500">*</span></label>
                      <div class="select__wrapper">
                        <Field name="organization.nameRu" v-slot="{ field, errorMessage }" v-model="projectDeveloperForm.organization.nameRu">
                          <input v-bind="field"
                                 type="text"
                                 id="organization-name-ru-input"
                                 class="input"
                                 :class="{'input--fail': errorMessage }"
                                 :placeholder="$lang.app.ENTER">
                          <span v-if="errorMessage" class="input__fail">{{ errorMessage }}</span>
                        </Field>
                      </div>
                    </div>
                  </div>
                  <div class="col-lg-6">
                    <div class="input__group">
                      <label for="organization-surname-input" class="label">{{ $lang.app.ORG_CHIEF_SURNAME }} <span class="text-red-500">*</span></label>
                      <Field name="organization.surname" v-slot="{ field, errorMessage }" v-model="projectDeveloperForm.organization.surname">
                        <input v-bind="field"
                               type="text"
                               id="organization-surname-input"
                               class="input"
                               :class="{'input--fail': errorMessage }"
                               :placeholder="$lang.app.ENTER">
                        <span v-if="errorMessage" class="input__fail">{{ errorMessage }}</span>
                      </Field>
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col-lg-6">
                    <div class="input__group">
                      <label for="organization-name-input" class="label">{{ $lang.app.ORG_CHIEF_NAME }} <span class="text-red-500">*</span></label>
                      <div class="select__wrapper">
                        <Field name="organization.name" v-slot="{ field, errorMessage }" v-model="projectDeveloperForm.organization.name">
                          <input v-bind="field"
                                 type="text"
                                 id="organization-name-input"
                                 class="input"
                                 :class="{'input--fail': errorMessage }"
                                 :placeholder="$lang.app.ENTER">
                          <span v-if="errorMessage" class="input__fail">{{ errorMessage }}</span>
                        </Field>
                      </div>
                    </div>
                  </div>
                  <div class="col-lg-6">
                    <div class="input__group">
                      <label for="organization-patronymic-input" class="label">{{ $lang.app.ORG_CHIEF_PATRONYMIC }}</label>
                      <Field name="organization.patronymic" v-slot="{ field, errorMessage }" v-model="projectDeveloperForm.organization.patronymic">
                        <input v-bind="field"
                               type="text"
                               id="organization-patronymic-input"
                               class="input"
                               :class="{'input--fail': errorMessage }"
                               :placeholder="$lang.app.ENTER">
                        <span v-if="errorMessage" class="input__fail">{{ errorMessage }}</span>
                      </Field>
                    </div>
                  </div>
                </div>
              </div>

              <div class="input__groups mb-4">
                <Field name="files" v-slot="{field}" v-model="projectDeveloperForm.files">
                  <FileInput v-bind="field"
                             :label="$lang.app.ADD_CERTIFICATE_OR_LICENSE_FILE"/>
                </Field>
                <ErrorMessage name="files" class="input__fail"/>
              </div>
              <div class="input__group">
                <label for="phone-number-input" class="label">{{ $lang.app.PHONE }}</label>
                <Field name="phoneNumber" v-slot="{ field, errorMessage }">
                  <input v-bind="field"
                         type="number"
                         id="phone-number-input"
                         v-model="projectDeveloperForm.phoneNumber"
                         class="input"
                         :class="{'input--fail': errorMessage }"
                         :placeholder="$lang.app.ENTER">
                  <span v-if="errorMessage" class="input__fail">{{ errorMessage }}</span>
                </Field>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    <div class="application-create__controls">
      <div class="container-fluid">
        <div class="application-create__controls__wrapper">
          <button type="button" @click="$router.go(-1)" class="button button--cancel">{{ $lang.app.CANCEL }}</button>
          <button type="submit" class="relative button"
                  :disabled="submitting">
            {{ $lang.app.SAVE }}
            <div v-if="submitting" class="loading-blue text-white absolute z-10 w-full rounded-lg h-full top-0 left-0 flex items-center justify-center">
              <div class="block animate-spin">
                <RefreshIcon class="w-6 h-6 mirror-y"/>
              </div>
            </div>
          </button>
        </div>
      </div>
    </div>
  </Form>
</template>
<script>
import * as yup from "yup";
import {Field, Form, ErrorMessage} from 'vee-validate';
import applicationApi from "../../../../api/applicant/application";
import applicantApi from "../../../../api/applicant/applicant";
import FileInput from "../../../../components/FileInput.vue";
import {
  all as userTypesAll,
  getValueByKey as userTypesGetValueByKey,
  getLabelRefByValue as userTypesGetLabelRefByValue,
} from "../../../../helpers/constants/userTypes";
import fileMixin from "../../../../mixins/fileMixin";

export default {
  name: 'ApplicantApplicationPlannersEdit',
  props: {
    application: Object
  },
  components: {
    Field,
    Form,
    ErrorMessage,
    FileInput,
  },
  mixins: [fileMixin],
  data() {
    return {
      submitting: false,

      types: this.userTypesAll().map(item => ({
        label: this.$lang.app[item.labelRef],
        value: item.value
      })),

      projectDeveloper: null,

      projectDeveloperInfo: null,

      projectDeveloperForm: null,

      projectDeveloperSchema: yup.object({
        type: yup.string().required(),
        certNumber: yup.string().required(),
        person: yup.mixed().when('type', {
          is: 1,
          then: yup.object({
            surname: yup.string().required(),
            name: yup.string().required(),
          }).required()
        }).when('type', {
          is: 3,
          then: yup.object({
            surname: yup.string().required(),
            name: yup.string().required(),
          }).required()
        }),
        organization: yup.mixed().when('type', {
          is: 2,
          then: yup.object({
            nameKg: yup.string().required(),
            nameRu: yup.string().required(),
            surname: yup.string().required(),
            name: yup.string().required(),
          }).required()
        }),
      }),
    }
  },
  created() {
    this.initProjectDeveloperForm()
  },
  methods: {
    userTypesAll,
    userTypesGetValueByKey,
    userTypesGetLabelRefByValue,

    initProjectDeveloperForm() {
      this.projectDeveloper = this.application.projectDevelopers.find(item => item.id == this.$route.params.plannerId)

      if(this.projectDeveloper) {
        this.projectDeveloperForm = {
          email: this.projectDeveloper.email,
          type: this.projectDeveloper.type,
          person: this.projectDeveloper.person ? {
            pin: this.projectDeveloper.person.pin,
            name: this.projectDeveloper.person.name,
            surname: this.projectDeveloper.person.surname,
            patronymic: this.projectDeveloper.person.patronymic,
          } : null,
          organization: this.projectDeveloper.organization ? {
            inn: this.projectDeveloper.organization.inn,
            nameKg: this.projectDeveloper.organization.nameKg,
            nameRu: this.projectDeveloper.organization.nameRu,
            name: this.projectDeveloper.organization.name,
            surname: this.projectDeveloper.organization.surname,
            patronymic: this.projectDeveloper.organization.patronymic,
          } : null,
          phoneNumber: this.projectDeveloper.phoneNumber,
          certNumber: this.projectDeveloper.certNumber,
          files: null,
        }

        this.loadProjectDeveloperFile(this.projectDeveloper, this.projectDeveloper.files)
      }
    },
    findProjectDeveloper() {
      this.projectDeveloperInfo = null
      applicantApi.findByEmail({email: this.projectDeveloperForm.email})
          .then(res => {
            this.projectDeveloperInfo = {
              type: res.userInfo.type,
              username: this.getProjectDeveloperFullName(res.userInfo)
            }
            this.setProjectDeveloperForm(res.userInfo)
          }).catch(error => {
        this.$snotify.error(error.response.data.message)
      })
    },

    loadProjectDeveloperFile(projectDeveloper, file) {
      applicationApi
          .getProjectDeveloperFile(this.application.id, projectDeveloper.id, file.id)
          .then(res => {
            return this.projectDeveloper.files = res.data
          })
          .catch((error) => {
            this.$snotify.error(error);
          });
    },

    setProjectDeveloperForm(userInfo) {
      this.resetProjectDeveloperForm()
      this.projectDeveloperForm.type = userInfo.type
      if(userInfo.type === this.userTypesGetValueByKey('INDIVIDUAL_ENTITY')) {
        this.projectDeveloperForm.person.pin = userInfo.person.pin
        this.projectDeveloperForm.person.surname = userInfo.person.surname
        this.projectDeveloperForm.person.name = userInfo.person.name
        this.projectDeveloperForm.person.patronymic = userInfo.person.patronymic
      }
      if(userInfo.type === this.userTypesGetValueByKey('LEGAL_ENTITY')) {
        this.projectDeveloperForm.organization.inn = userInfo.organization.inn
        this.projectDeveloperForm.organization.nameKg = userInfo.organization.nameKg
        this.projectDeveloperForm.organization.nameRu = userInfo.organization.nameRu
        this.projectDeveloperForm.organization.surname = userInfo.organization.surname
        this.projectDeveloperForm.organization.name = userInfo.organization.name
        this.projectDeveloperForm.organization.patronymic = userInfo.organization.patronymic
      }
      if(userInfo.type === this.userTypesGetValueByKey('INDIVIDUAL_ENTREPRENEUR')) {
        this.projectDeveloperForm.person.surname = userInfo.individualEnterprise.surname
        this.projectDeveloperForm.person.name = userInfo.individualEnterprise.name
        this.projectDeveloperForm.person.patronymic = userInfo.individualEnterprise.patronymic
      }
    },

    resetProjectDeveloperForm() {
      this.projectDeveloperForm = {
        email: this.projectDeveloperForm.email,
        type: null,
        person: {
          pin: '',
          surname: '',
          name: '',
          patronymic: '',
        },
        organization: {
          inn: '',
          nameKg: '',
          nameRu: '',
          surname: '',
          name: '',
          patronymic: '',
        },
        phoneNumber: '',
        certNumber: null,
      }
    },

    getProjectDeveloperFullName(userInfo) {
      if(userInfo.type === this.userTypesGetValueByKey('INDIVIDUAL_ENTITY')) {
        return userInfo.person.surname + ' ' + userInfo.person.name + ' ' + userInfo.person.patronymic
      }
      if(userInfo.type === this.userTypesGetValueByKey('LEGAL_ENTITY')) {
        return this.$lang.$translate({ru: userInfo.organization.nameRu, kg: userInfo.organization.nameKg})
      }
      if(userInfo.type === this.userTypesGetValueByKey('INDIVIDUAL_ENTREPRENEUR')) {
        return userInfo.individualEnterprise.surname + ' ' + userInfo.individualEnterprise.name + ' ' + userInfo.individualEnterprise.patronymic
      }
    },

    async submit(values, actions) {
      this.submitting = true
      applicationApi.updateProjectDeveloper(this.$route.params.applicationId, this.$route.params.plannerId, {
        ...this.projectDeveloperForm,
        files: this.projectDeveloperForm.files ? {
          filename: this.projectDeveloperForm.files.name,
          fileData: await this.getBase64(this.projectDeveloperForm.files),
        } : {
          filename: this.projectDeveloper.files.filename,
          fileData: null,
        }
      })
          .then(() => {
            this.$router.go(-1)
            this.$snotify.success(this.$lang.app.SUCCESS_ACTION)
          })
          .catch(error => {
            if (error.response.status === 422) {
              this.setValidationErrors(actions, error.response.data.errors)
            } else {
              this.$snotify.error(error)
            }
          }).finally(() => {
            this.submitting = false
      })
    }
  }
}
</script>
